import { useState, useEffect, useRef } from 'react';

interface BGMConfig {
  src: string;
  volume?: number;
  loop?: boolean;
}

export const useBGM = (config: BGMConfig) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(config.volume || 0.5);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    // Audio 객체 생성
    audioRef.current = new Audio(config.src);
    audioRef.current.volume = volume;
    audioRef.current.loop = config.loop || true;
    audioRef.current.preload = 'auto';

    // 이벤트 리스너 설정
    const audio = audioRef.current;
    
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
    };
  }, [config.src, config.loop, volume]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const play = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(error => {
        console.error('BGM 재생 실패:', error);
      });
    }
  };

  const pause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const setVolumeLevel = (newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const togglePlay = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };

  return {
    isPlaying,
    volume,
    isMuted,
    play,
    pause,
    stop,
    setVolumeLevel,
    toggleMute,
    togglePlay,
  };
};
